/**
 * Real-world theatres — historical maps of Europe built offline from Natural Earth plus curated
 * sources (see docs/Theatres.md and scripts/build-theatres.ts) and committed as compact JSON.
 * The run time never touches Natural Earth: `loadTheatreMap(id)` decodes the committed data into
 * a GameMap (pure, deterministic, cached), so `SessionConfig.theatre` reproduces the same map in
 * every host and replay.
 *
 * Nations are the belligerents of the era in English (the client re-words them in German); land
 * that no belligerent owned is neutral and closed (sim/movecost.ts `isClosed`).
 */
import { MAP_SIZES } from '../core/constants';
import { hashString } from '../core/rng';
import type { GameMap, SessionConfig } from '../core/types';
import { type FactionIdentity, generateFactionIdentities, generateMap } from '../mapgen';
import { decodeTheatre, type TheatreData } from './codec';
import eastPrussia1914 from './data/east-prussia-1914.json';
import europe1939 from './data/europe-1939.json';
import moscow1941 from './data/moscow-1941.json';
import normandy1944 from './data/normandy-1944.json';
import poland1939 from './data/poland-1939.json';
import stalingrad1942 from './data/stalingrad-1942.json';
import { THEATRES, type TheatreDef, type TheatreId, type TheatreNation } from './registry';

export * from './registry';

const DATA: Record<TheatreId, TheatreData> = {
  'poland-1939': poland1939 as TheatreData,
  'east-prussia-1914': eastPrussia1914 as TheatreData,
  'europe-1939': europe1939 as TheatreData,
  'moscow-1941': moscow1941 as TheatreData,
  'stalingrad-1942': stalingrad1942 as TheatreData,
  'normandy-1944': normandy1944 as TheatreData,
};

export const isTheatreId = (id: unknown): id is TheatreId =>
  typeof id === 'string' && Object.hasOwn(THEATRES, id);

/** The theatre definition, or a clear error for an unknown id (e.g. a config from a newer build). */
export function theatreDef(id: string): TheatreDef {
  if (!isTheatreId(id)) throw new Error(`unknown theatre "${id}"`);
  return THEATRES[id];
}

const cache = new Map<TheatreId, GameMap>();

function copyMap(m: GameMap): GameMap {
  return {
    ...m,
    terrain: m.terrain.slice(),
    elevation: m.elevation.slice(),
    moisture: m.moisture.slice(),
    riverEdges: m.riverEdges.slice(),
    rivers: m.rivers.map((r) => ({ points: r.points.slice(), widths: r.widths.slice() })),
    roads: m.roads.slice(),
    rails: m.rails.slice(),
    cities: m.cities.map((c) => ({ ...c })),
    cityAt: m.cityAt.slice(),
    initialOwner: m.initialOwner.slice(),
    region: m.region.slice(),
    regions: m.regions.map((r) => ({ ...r })),
  };
}

/**
 * The theatre's map. Decoded once and cached; every call returns a fresh copy, so a caller that
 * edits its map (tests, tools) never changes anyone else's.
 */
export function loadTheatreMap(id: TheatreId | string): GameMap {
  const def = theatreDef(id);
  let map = cache.get(def.id);
  if (!map) {
    map = decodeTheatre(DATA[def.id], def.name, hashString(def.id));
    if (map.factionCount !== def.nations.length)
      throw new Error(`theatre ${id}: data has ${map.factionCount} nations, registry ${def.nations.length}`);
    cache.set(def.id, map);
  }
  return copyMap(map);
}

/** Names, adjectives and colours of a theatre's nations, by faction id. */
export function theatreIdentities(id: TheatreId | string): TheatreNation[] {
  return theatreDef(id).nations.map((n) => ({ ...n }));
}

/**
 * The map a config plays on: its theatre's, or the one generated from its seed and size. Every
 * place that rebuilds a match from its config (session, replays, viewers) goes through here.
 */
export function mapForConfig(config: SessionConfig): GameMap {
  if (config.theatre !== undefined) return loadTheatreMap(config.theatre);
  const size = MAP_SIZES[config.mapSize] ?? MAP_SIZES.medium;
  return generateMap({
    seed: config.seed,
    width: size.width,
    height: size.height,
    factionCount: config.factions.length,
  });
}

/** Names, adjectives (and theatre colours) of a config's nations, by faction id. */
export function identitiesForConfig(config: SessionConfig): FactionIdentity[] {
  if (config.theatre !== undefined) return theatreIdentities(config.theatre);
  return generateFactionIdentities(config.seed, config.factions.length);
}
